import React, { useMemo } from 'react';
import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@tapresearch/ui/components';
import { TapAnswer, TapQuestionResult } from '../../../../api';

interface RankingQuestionTableProps {
  usePercentage: boolean;
  results: TapQuestionResult['attributes']['results']['answers'];
  tapAnswers: TapAnswer[];
}
export const RankingQuestionTable = ({
  usePercentage,
  results,
  tapAnswers,
}: RankingQuestionTableProps) => {
  const sortedAnswers = useMemo(
    () =>
      Object.entries(results).sort(
        (a, b) => a[1]?.average_rank - b[1]?.average_rank
      ),
    [results]
  );

  const tapAnswersLabelsHash = useMemo(
    () =>
      tapAnswers.reduce<Record<string, string>>((acc, answer) => {
        return {
          ...acc,
          [answer.attributes.id.toString()]: answer.attributes.option_text,
        };
      }, {}),
    [tapAnswers]
  );

  return (
    <Box
      data-testid="ranking-result-table"
      flexGrow={1}
      my={{ base: 5, md: 8 }}
      w="full"
      overflowX="scroll"
      overflowY="scroll"
      maxH="500px"
    >
      <Table
        variant="simple"
        sx={{
          borderCollapse: 'separate',
          borderSpacing: '0',
        }}
      >
        <Thead>
          <Tr
            sx={{
              th: {
                textTransform: 'none',
                color: 'gray.900',
                fontWeight: 'normal',
                fontSize: 'sm',
                letterSpacing: 0,
                position: 'sticky',
                top: 0,
                bg: 'white',
              },
            }}
          >
            <Th>Rank</Th>
            <Th>Item</Th>
            <Th>Avg rank</Th>
            {tapAnswers.map((_, i) => (
              <Th key={i} textAlign="center">
                #{i + 1}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {sortedAnswers.map(([answerId, answer], rankIndex) => {
            const { average_rank: averageRank, ranks, total } = answer;
            return (
              <Tr key={rankIndex}>
                <Td w="80px" textAlign="center">
                  {rankIndex + 1}
                </Td>
                <Td w="25%" minW="250px">
                  <Text
                    data-testid="item-option-text"
                    {...{
                      py: 1,
                      px: 2,
                      color: 'blue.500',
                      bg: 'blue.100',
                      fontSize: 'sm',
                      fontWeight: 'medium',
                      rounded: 'md',
                      d: 'inline-flex',
                    }}
                  >
                    {tapAnswersLabelsHash[answerId]}
                  </Text>
                </Td>
                <Td
                  data-testid="average-rank-column"
                  {...{
                    w: '20%',
                    minW: '100px',
                    fontWeight: 'medium',
                    letterSpacing: 'tight',
                  }}
                >
                  {averageRank}
                </Td>
                {ranks &&
                  ranks.map((numRespondents, i) => (
                    <Td
                      key={`${answerId}-${i}`}
                      {...{
                        letterSpacing: 'tight',
                        textAlign: 'center',
                      }}
                    >
                      {usePercentage
                        ? `${(100 * (numRespondents / total)).toFixed(2)}%`
                        : numRespondents}
                    </Td>
                  ))}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};
